import {
  Component,
  Input,
  forwardRef,
  ElementRef,
  ViewChild,
  HostListener,
  ChangeDetectorRef,
  OnInit,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';
import { coinIcon, FALLBACK_COIN_ICON, useFallbackIcon } from '@olv/ui-shared/utils/asset-balance.utils';

@Component({
  selector: 'app-blockchain-dropdown',
  standalone: true,
  imports: [FormsModule, MatIconModule],
  templateUrl: './blockchain-dropdown.component.html',
  styleUrl: './blockchain-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BlockchainDropdownComponent),
      multi: true,
    },
  ],
})
export class BlockchainDropdownComponent implements ControlValueAccessor, OnInit {
  protected readonly useFallbackIcon = useFallbackIcon;
  @Input() label: string | undefined;
  @Input() placeholder = 'Select an option';
  @Input() options: { value: any; label: string }[] = [];
  @Input() isInvalid = false;
  @Input() errorMessage: string | undefined;
  @Input() disabled = false;

  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;

  @Input() value: any = null;
  isOpen = false;
  hasOpenedDropdown = false;
  selectedLabel = '';

  // Control Value Accessor methods
  onChange: any = () => {};
  onTouched: any = () => {};

  private cdr = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef);
  protected readonly metadata = inject(NetworkMetadataService);

  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedLabel();
    this.cdr.markForCheck(); // Explicitly mark for change detection
  }

  ngOnInit(): void {
    this.metadata.load();
    // Ensure the component initializes with the correct label and icon
    this.updateSelectedLabel();
    this.cdr.markForCheck(); // Explicitly mark for change detection
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  updateSelectedLabel(): void {
    const selectedOption = this.options.find((option) => option.value === this.value);
    this.selectedLabel = selectedOption ? selectedOption.label : '';
  }

  hasValue(): boolean {
    return this.value !== null && this.value !== undefined && this.value !== '';
  }

  toggleDropdown(): void {
    if (this.disabled) return;
    const openedDropdown = this.isOpen;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.onTouched();
    }
    
    if (openedDropdown && !this.isOpen) {
      this.hasOpenedDropdown = true;
    }
  }

  selectOption(option: { value: any; label: string }): void {
    this.value = option.value;
    this.selectedLabel = option.label;
    this.isOpen = false;
    this.hasOpenedDropdown = true;
    this.onChange(this.value);
    this.cdr.markForCheck(); // Explicitly mark for change detection
  }

  /**
   * Get the appropriate icon path based on the blockchain value
   */
  getBlockchainIconPath(value: any): string {
    const network = value == null ? undefined : this.metadata.network(value);
    return network ? coinIcon(network.iconKey) : FALLBACK_COIN_ICON;
  }

  /**
   * Get the icon path for the currently selected value
   */
  get selectedIconPath(): string {
    // Always show an icon, even when no value is selected
    return this.getBlockchainIconPath(this.value);
  }

  /**
   * Check if we should show the icon (always true for blockchain dropdown)
   */
  get shouldShowIcon(): boolean {
    return true;
  }

  get isShowError(): boolean {
    return this.isInvalid && this.hasOpenedDropdown && !this.hasValue() && !this.isOpen;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.hasOpenedDropdown = true;
    }
  }
}